import { AppProvider, Page, Card, BlockStack, Text, List } from '@shopify/polaris'
import enTranslations from '@shopify/polaris/locales/en.json'

const LAST_UPDATED = '2026-07-04'
const CONTACT_EMAIL = 'redlofmuddasir1888@gmail.com'

/**
 * Fully static, publicly reachable with zero Shopify session context —
 * loadable directly at /privacy in a plain browser tab, unauthenticated,
 * for App Store reviewers and merchants alike. Placeholder content
 * (legal entity name, retention periods) should be confirmed before
 * public App Store submission — this is a reasonable starting draft,
 * not reviewed legal counsel.
 */
export function PrivacyPolicyPage() {
  return (
    <AppProvider i18n={enTranslations}>
      <div style={{ maxWidth: '760px', margin: '0 auto', padding: '32px 16px' }}>
        <Page title="Privacy Policy">
          <BlockStack gap="400">
            <Card>
              <BlockStack gap="200">
                <Text as="p" tone="subdued">Last updated: {LAST_UPDATED}</Text>
                <Text as="p">
                  GrowthPilot AI ("we", "our", "the app") is a Shopify application that audits store
                  content, generates AI-powered recommendations, and helps merchants improve their
                  store's SEO, trust signals, and sales performance. This policy explains what data
                  we access, why, and how it is handled.
                </Text>
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="200">
                <Text as="h2" variant="headingMd">Data we access</Text>
                <List type="bullet">
                  <List.Item><b>Products, pages, and themes</b> — read to power store audits, SEO analysis, and AI copywriting; some fields (SEO title/description, page content) may be updated only with your explicit review and approval via the Auto Fix feature.</List.Item>
                  <List.Item><b>Orders and customers</b> — read to compute the Sales &amp; Customer Insights feature (revenue trends, top products, top customers). Order and customer data is used transiently to generate a snapshot report and is not shared with third parties beyond the AI processing described below.</List.Item>
                  <List.Item><b>Store content (write access)</b> — used only to apply changes you explicitly approve (product SEO fixes, page SEO fixes, or newly drafted legal pages, which are always created unpublished for your review before going live).</List.Item>
                </List>
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="200">
                <Text as="h2" variant="headingMd">AI processing</Text>
                <Text as="p">
                  GrowthPilot AI uses Anthropic's Claude models to generate recommendations, product
                  copy, keyword research, competitor and marketing analysis, sales insights, and draft
                  legal page content. Store data relevant to each specific request (e.g. a product's
                  title and description) is sent to Anthropic's API for processing. This data is used
                  solely to generate the response for your request.
                </Text>
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="200">
                <Text as="h2" variant="headingMd">Data retention</Text>
                <Text as="p">
                  Synced product and page data is retained while the app is installed on your store
                  and removed shortly after uninstallation. AI usage logs and generated reports are
                  retained to support billing and customer support. You may request deletion of your
                  data at any time via the contact details below.
                </Text>
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="200">
                <Text as="h2" variant="headingMd">Your rights (GDPR / CCPA)</Text>
                <Text as="p">
                  You have the right to request access to, correction of, or deletion of your data.
                  GrowthPilot AI implements Shopify's mandatory compliance webhooks
                  (customer data request, customer redact, and shop redact) to support these rights.
                </Text>
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="200">
                <Text as="h2" variant="headingMd">Contact</Text>
                <Text as="p">
                  Questions about this policy or your data? Email us at {CONTACT_EMAIL}.
                </Text>
              </BlockStack>
            </Card>
          </BlockStack>
        </Page>
      </div>
    </AppProvider>
  )
}
